import type { FileInfo } from '@/shared/types/files';
import type {
  ChatAttachment,
  ChatDetail,
  ChatDetailAttachment,
  ChatMessage,
  ChatParticipant,
  ChatThread,
  ChatUser,
} from '../model/types';
import type {
  ChatDetailAttachmentDTO,
  ChatDetailDTO,
  GroupChatRowDTO,
  CreatePersonalChatResultDTO,
  ChatListItemDTO,
  ChatListParticipantDTO,
  ChatLastMessagePreviewDTO,
  ChatPinnedMessageDTO,
  ChatMessageDTO,
  ChatParticipantDTO,
  ChatUserDTO,
} from './dto';
import { buildParticipantPresenceLabel } from '../lib/formatParticipantPresence';
import type { ChatRuntime } from './runtime';

function resolveOutgoingStatus(
  isOwn: boolean,
  currentUserId: number,
  readByUserIds?: number[],
): 'sent' | 'read' | undefined {
  if (!isOwn) {
    return undefined;
  }

  const readByOthers = (readByUserIds ?? []).some((userId) => userId !== currentUserId);
  return readByOthers ? 'read' : 'sent';
}

function inferAttachmentKind(mime: string): ChatAttachment['kind'] {
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  return 'file';
}

function getRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

const CHAT_PARTICIPANT_ROLES = new Set(['owner', 'admin', 'member']);

function resolveUserDisplayName(dto: ChatUserDTO): string {
  const fullNameFromParts = [dto.firstName, dto.lastName].filter(Boolean).join(' ').trim();
  return (
    fullNameFromParts ||
    readString(dto.username) ||
    readString(dto.fullName) ||
    'User'
  );
}

function resolveParticipantChatRole(dto: { chatRole?: string }): string {
  const chatRole = dto.chatRole?.trim().toLowerCase();
  if (chatRole && CHAT_PARTICIPANT_ROLES.has(chatRole)) {
    return chatRole;
  }

  return 'member';
}

export function mapChatUser(dto: ChatUserDTO, runtime: ChatRuntime): ChatUser {
  const fullNameFromParts = [dto.firstName, dto.lastName].filter(Boolean).join(' ').trim();
  const username =
    (readString(dto.username) ?? readString(dto.fullName) ?? fullNameFromParts) || 'User';
  const accountType = readString(dto.accountType) ?? readString(dto.customRole) ?? '';

  return {
    id: dto.id,
    firstName: dto.firstName,
    lastName: dto.lastName,
    username,
    accountType,
    avatarUrl: runtime.resolveFileUrl(dto.avatar),
    badge: dto.badge ?? null,
    ...(dto.isOnline !== undefined
      ? {
          isOnline: dto.isOnline === true,
          lastSeenAt: dto.lastSeenAt ? new Date(dto.lastSeenAt) : null,
        }
      : {}),
  };
}

/** Ссылки приватного файла — уже абсолютные presigned-URL, резолвить их не нужно. */
function mapAttachment(dto: FileInfo): ChatAttachment {
  return {
    key: dto.key,
    name: dto.name,
    size: dto.size,
    mime: dto.mime,
    url: dto.url,
    thumbnailUrl: dto.thumbnailUrl,
    urlExpiresAt: dto.urlExpiresAt,
    kind: inferAttachmentKind(dto.mime),
  };
}

function mapDetailAttachment(dto: ChatDetailAttachmentDTO): ChatDetailAttachment {
  return {
    ...mapAttachment(dto),
    messageId: dto.messageId,
    createdAt: dto.createdAt ? new Date(dto.createdAt) : null,
  };
}

function buildListItemContext(dto: {
  context?: Record<string, unknown>;
  origin?: string;
  title?: string | null;
}): Record<string, unknown> {
  const context: Record<string, unknown> = { ...(dto.context ?? {}) };

  if (dto.origin) {
    context.origin = dto.origin;
  }

  const title = readString(dto.title);
  if (title && !readString(context.title)) {
    context.title = title;
  }

  return context;
}

function buildThreadTitle(dto: ChatListItemDTO): string {
  const apiTitle = readString(dto.title);
  if (apiTitle) {
    return apiTitle;
  }

  if (dto.type === 'personal') {
    const user = getRecord(dto.context?.user);
    const firstName = readString(user?.firstName);
    const lastName = readString(user?.lastName);
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    return fullName || readString(user?.username) || 'Direct chat';
  }

  const context = getRecord(dto.context);

  return (
    readString(context?.title) ||
    readString(context?.name) ||
    readString(context?.moduleTitle) ||
    readString(getRecord(context?.module)?.title) ||
    readString(getRecord(context?.group)?.title) ||
    'Group chat'
  );
}

function buildThreadSubtitle(dto: ChatListItemDTO): string | undefined {
  if (dto.type === 'personal') {
    const user = getRecord(dto.context?.user);
    if (user) {
      const firstName = readString(user.firstName);
      const lastName = readString(user.lastName);
      const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
      return (
        fullName ||
        readString(user.username) ||
        readString(user.accountType) ||
        readString(dto.title) ||
        undefined
      );
    }

    const peer = dto.lastMessage?.sender;
    if (peer) {
      const fullName = [peer.firstName, peer.lastName].filter(Boolean).join(' ').trim();
      return fullName || peer.username || readString(dto.title) || undefined;
    }

    return readString(dto.title) || undefined;
  }

  const context = getRecord(dto.context);

  return (
    readString(context?.subtitle) ||
    readString(context?.description) ||
    readString(getRecord(context?.module)?.code) ||
    undefined
  );
}

export function mapPinnedMessage(
  dto: ChatPinnedMessageDTO,
  chatId: number,
  currentUserId: number,
  runtime: ChatRuntime,
): ChatMessage {
  const isOwn = dto.sender?.id === currentUserId;
  const authorName = dto.sender ? resolveUserDisplayName(dto.sender) : 'System';

  return {
    id: dto.id,
    kind: 'user',
    conversationId: chatId,
    authorId: dto.sender?.id ?? null,
    authorName,
    sentAt: new Date(dto.createdAt),
    text: dto.text,
    attachments: [],
    replyTo: null,
    readByMe: true,
    isOwn,
  };
}

function buildLastMessagePreview(lastMessage: ChatLastMessagePreviewDTO | null): string {
  if (!lastMessage) return 'No messages yet';
  if (lastMessage.text?.trim()) return lastMessage.text.trim();
  return 'Attachment';
}

export function resolvePersonalChatId(dto: CreatePersonalChatResultDTO): number {
  if (typeof dto.id === 'number') {
    return dto.id;
  }

  const legacyChatId = (dto as { chatId?: number }).chatId;
  if (typeof legacyChatId === 'number') {
    return legacyChatId;
  }

  throw new Error('Personal chat response is missing chat id');
}

export function mapGroupChatRow(dto: GroupChatRowDTO, runtime: ChatRuntime): ChatThread {
  const listItem: ChatListItemDTO = {
    id: dto.id,
    type: dto.type,
    status: dto.status,
    avatar: dto.avatar,
    title: dto.title,
    origin: dto.origin,
    context: buildListItemContext(dto),
    lastMessage: dto.lastMessage,
    unreadCount: dto.unreadCount,
    lastMessageAt: dto.lastMessageAt,
    canLeave: dto.canLeave,
    mutedUntil: dto.mutedUntil ?? null,
    isArchived: dto.isArchived,
    pinnedMessage: dto.pinnedMessage ?? null,
  };

  return mapThread(listItem, runtime);
}

function mapListParticipant(
  participant: ChatListParticipantDTO | null | undefined,
): Pick<ChatThread, 'peerUserId' | 'online' | 'lastSeenAt' | 'lastSeenLabel'> {
  if (!participant) {
    return {};
  }

  const presence = buildParticipantPresenceLabel({
    isOnline: participant.isOnline,
    lastSeenAt: participant.lastSeenAt,
  });

  return {
    peerUserId: participant.userId,
    online: presence.online,
    lastSeenAt: participant.lastSeenAt ? new Date(participant.lastSeenAt) : null,
    lastSeenLabel: presence.lastSeenLabel,
  };
}

export function mapThread(
  dto: ChatListItemDTO,
  runtime: ChatRuntime,
  currentUserId?: number,
): ChatThread {
  return {
    id: dto.id,
    kind: dto.type,
    title: buildThreadTitle(dto),
    subtitle: buildThreadSubtitle(dto),
    lastMessagePreview: buildLastMessagePreview(dto.lastMessage),
    updatedAt: dto.lastMessageAt ? new Date(dto.lastMessageAt) : null,
    unreadCount: dto.unreadCount,
    avatarUrl: runtime.resolveFileUrl(dto.avatar) ?? undefined,
    ...mapListParticipant(dto.participant),
    status: dto.status,
    canLeave: dto.canLeave === true,
    mutedUntil: dto.mutedUntil ? new Date(dto.mutedUntil) : null,
    isArchived: dto.isArchived === true,
    pinnedMessage:
      dto.pinnedMessage && typeof currentUserId === 'number'
        ? mapPinnedMessage(dto.pinnedMessage, dto.id, currentUserId, runtime)
        : null,
    context: buildListItemContext(dto),
  };
}

function mapParticipant(dto: ChatParticipantDTO, runtime: ChatRuntime): ChatParticipant {
  return {
    participantId: dto.participantId,
    user: mapChatUser(dto.user, runtime),
    chatRole: resolveParticipantChatRole(dto),
    membershipStatus: dto.status,
    joinedAt: new Date(dto.joinedAt),
  };
}

export function mapChatDetail(
  dto: ChatDetailDTO,
  runtime: ChatRuntime,
  currentUserId: number,
): ChatDetail {
  return {
    id: dto.id,
    kind: dto.type,
    title: readString(dto.title) || 'Chat',
    status: dto.status,
    avatarUrl: runtime.resolveFileUrl(dto.avatar),
    canLeave: dto.canLeave === true,
    mutedUntil: dto.mutedUntil ? new Date(dto.mutedUntil) : null,
    pinnedMessage: dto.pinnedMessage
      ? mapPinnedMessage(dto.pinnedMessage, dto.id, currentUserId, runtime)
      : null,
    context: buildListItemContext(dto),
    participants: (dto.participants ?? []).map((participant) => mapParticipant(participant, runtime)),
    unreadCount: dto.unreadCount,
    updatedAt: dto.lastMessageAt ? new Date(dto.lastMessageAt) : null,
    images: dto.images.map((item) => mapDetailAttachment(item)),
    videos: dto.video.map((item) => mapDetailAttachment(item)),
    files: dto.files.map((item) => mapDetailAttachment(item)),
  };
}

export function mapMessage(
  dto: ChatMessageDTO,
  conversationId: number,
  currentUserId: number,
  runtime: ChatRuntime,
): ChatMessage {
  const isOwn = dto.sender?.id === currentUserId;
  const authorName = dto.sender ? resolveUserDisplayName(dto.sender) : 'System';

  return {
    id: dto.id,
    kind: dto.type,
    conversationId,
    authorId: dto.sender?.id ?? null,
    authorName,
    sentAt: new Date(dto.createdAt),
    text: dto.text,
    attachments: dto.attachments.map((item) => mapAttachment(item)),
    replyTo:
      dto.replyTo && typeof dto.replyTo.id === 'number'
        ? {
            id: dto.replyTo.id,
            text: dto.replyTo.text,
            attachments: dto.replyTo.attachments.map((item) => mapAttachment(item)),
            sender: dto.replyTo.sender ? mapChatUser(dto.replyTo.sender, runtime) : null,
          }
        : null,
    readByMe: dto.readByMe,
    isOwn,
    outgoingStatus: resolveOutgoingStatus(isOwn, currentUserId, dto.readByUserIds),
    clientMessageId: dto.clientMessageId ?? undefined,
  };
}
